import { GlobalStore } from '@roc/client-portal-shared/stores';
import { GENERIC_ERROR_MESSAGE } from '@roc/client-portal-shared/utils';
import { action, flow, makeObservable, observable } from 'mobx';
import { ApiResponse } from '@roc/feature-app-core';
import { Option } from '@roc/ui/utils';
import { RequestIdVerificationService } from '../services/requestIdVerificationService';

export interface MessageConfig {
  link: string,
  tpoName: string,
  fromEmail: string,
  host: string,
}

export interface DecodedParams {
  lenderId: number,
  originatorId: number,
  borrowerId: number,
}

export class RequestIdVerificationStore {
  globalStore: GlobalStore;
  requestIdVerificationService: RequestIdVerificationService;

  borrowerOptions: Option[] = [];
  originatorId: number;
  messageConfig: MessageConfig;
  borrowerDetailsVerification: any;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.requestIdVerificationService = new RequestIdVerificationService();
    makeObservable(this, {
      borrowerOptions: observable,
      messageConfig: observable,
      borrowerDetailsVerification: observable,
      findBorrowersByName: flow,
      findBorrowersAndLeadsByName: flow,
      fetchRecordData: action,
      getMessageConfig: flow,
      sendRequestIdVerificationEmail: flow,
    });
  }

  reset() {
    this.borrowerOptions = [];
    this.originatorId = null;
    this.messageConfig = null;
    this.borrowerDetailsVerification = null;
  }

  *getMessageConfig(borrowerId: number, lenderId: number = null) {
    try {
      this.reset();
      const response: ApiResponse = yield this.requestIdVerificationService.getMessageConfig(this.globalStore?.portalConfiguration?.host, borrowerId, lenderId);
      this.messageConfig = response?.data?.data;
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting id config.',
      });
    }
  }

  *sendRequestIdVerificationEmail(toEmail: string, subject: string, bodyContent: string, callback?: () => void) {
    try {
      yield this.requestIdVerificationService.sendRequestIdVerificationEmail({
        host: this.messageConfig?.host || this.globalStore?.portalConfiguration?.host,
        fromEmail: this.messageConfig?.fromEmail,
        toEmail,
        subject,
        bodyContent: bodyContent,
        tpoName: this.messageConfig?.tpoName,
      });
      callback && callback();
    }
    catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending id verification email.',
      });
    }
  }

  *findBorrowersAndLeadsByName(name, cancelToken?) {
    try {
      if (name.length >= 3) {
        const response = yield this.requestIdVerificationService.findBorrowersAndLeadsByName(
          name,
          cancelToken
        );
        this.borrowerOptions = response.data.data;
      } else {
        this.borrowerOptions = [];
      }
    } catch (e) {
      console.log(e);
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  *findBorrowersByName(name, cancelToken?) {
    try {
      if (name.length >= 3) {
        const response = yield this.requestIdVerificationService.findBorrowersByName(
          name,
          cancelToken
        );
        const options = response?.data?.data;
        this.borrowerOptions = options ? options.filter(item => item.value.includes("borrower")) : [];
      } else {
        this.borrowerOptions = [];
      }
    } catch (e) {
      console.log(e);
      if (!e.isCancel) {
        this.globalStore.notificationStore.showErrorNotification({
          message: GENERIC_ERROR_MESSAGE,
        });
      }
    }
  }

  async fetchRecordData(recordId) {
    try {
      const recordComposedKey = recordId.split('-');
      const type = recordComposedKey[0];
      const id = recordComposedKey[1];
      const response = type === 'lead'
        ? await this.requestIdVerificationService.fetchLeadData(id, '')
        : await this.requestIdVerificationService.fetchBorrowerData(id);

      const recordData = response.data;
      const formData = {
        leadId: recordData.leadId,
        borrowerId: recordData.borrowerId,
        email: type === 'lead' ? recordData.email : recordData.emailAddress,
        phone: type === 'lead' ? recordData.phone : recordData.cellPhone ? recordData.cellPhone : (recordData.homePhone ?? ''),
        name: recordData.firstName + ' ' + recordData.lastName,
        firstname: recordData.firstName,
        lastname: recordData.lastName,
      };

      return formData;
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

}